import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Loader2, Inbox, type LucideIcon,
  FileText, Wrench, ArrowLeftRight, Bell, Package, BarChart3, Users, MapPin, Truck,
  Receipt, ClipboardList, Calendar, Building2, Tag, Settings, ShoppingCart, Mail, Phone,
  ExternalLink, Download, AlertCircle as AlertCircleIcon,
} from 'lucide-react'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import type { BPlateViewResult, BPlateChartType, BPlateViewColumnDef, BPlateViewRowAction, BPlateViewFormField, BCoffeeValidationError, BPlateViewSummaryRow } from '../types'
import { triggerJourney, getJourney, getJourneyInterviewer, submitJourneyInterviewer, stopJourney } from '../api/brainer'
import { getDocument, getDocumentBlobUrl, viewDocumentInTab, downloadDocument } from '../api/storage'
import { InterviewerWizard } from './InterviewerWizard'
import { Modal } from './Modal'
import { Button } from './ui/button'
import { useDateFmt } from '../hooks/useDateFmt'

// TailAdmin chart palette: brand, blue-light, success, warning, error, theme-purple, theme-pink, gray-400.
const CHART_COLORS = ['#465fff', '#0ba5ec', '#12b76a', '#f79009', '#f04438', '#7a5af8', '#ee46bc', '#98a2b3']

export function getByDotPath(obj: unknown, path: string): unknown {
  if (!path) return obj
  // Try the whole path as one literal key first — Brainer's own bean-key convention is
  // "<Cup Title>.<field>" (e.g. "EnergyOS Device.vendor"), a flat key that happens to contain a
  // dot, not a nested path. Splitting on "." unconditionally would treat every such column key
  // as a two-level nested lookup and always resolve to undefined, since there's no actual
  // nested object at obj['EnergyOS Device']. Only fall back to dot-split traversal (genuine
  // nested paths, e.g. a dataPath like "result.items") when no exact key matches.
  if (typeof obj === 'object' && obj !== null && !Array.isArray(obj) && path in (obj as Record<string, unknown>)) {
    return (obj as Record<string, unknown>)[path]
  }
  const parts = path.split('.')
  let cur = obj
  for (const p of parts) {
    if (cur === null || cur === undefined || typeof cur !== 'object' || Array.isArray(cur)) return undefined
    cur = (cur as Record<string, unknown>)[p]
  }
  return cur
}

// Threaded down to PreparationView / RowActionModal / RowActionJourney / ButtonView instead of
// those components importing triggerJourney/getJourney/etc directly, so the public OpenPlace
// token page (no JWT, no authenticated axios interceptor) can supply token-scoped equivalents —
// same pattern as PlateLayout's runView prop. getInterviewer/submitInterviewer are left pointing
// at the authenticated endpoints even on the token page (see OpenPlaceTokenView.tsx): Phase 3b's
// Preparations are single-Straw-step only, so that path is never actually exercised there.
export interface JourneyApi {
  trigger: typeof triggerJourney
  get: typeof getJourney
  getInterviewer: typeof getJourneyInterviewer
  submitInterviewer: typeof submitJourneyInterviewer
}

export const DEFAULT_JOURNEY_API: JourneyApi = {
  trigger: triggerJourney,
  get: getJourney,
  getInterviewer: getJourneyInterviewer,
  submitInterviewer: submitJourneyInterviewer,
}

interface Props {
  result: BPlateViewResult
  runInputs?: Record<string, string>
  journeyApi?: JourneyApi
}

function resolveTemplate(template: string, row: Record<string, unknown>): string {
  return template.replace(/\$\$([\w.]+)\$\$/g, (_, key) => String(getByDotPath(row, key) ?? ''))
}

export function EmptyState({ icon: Icon = Inbox, message }: { icon?: LucideIcon; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 py-8 text-ink-dim">
      <Icon size={20} strokeWidth={1.5} />
      <p className="text-xs">{message}</p>
    </div>
  )
}

// Opt-in per-column pill rendering for categorical/status values (e.g. {"active": "emerald",
// "closed": "slate"}) — a data-value affordance, distinct from any Plate-level theme: only the
// column that sets badgeMap changes how its own values are drawn, nothing else on the Plate does.
const BADGE_CLASS: Record<string, string> = {
  slate: 'plate-badge-slate', blue: 'plate-badge-blue', emerald: 'plate-badge-emerald',
  amber: 'plate-badge-amber', red: 'plate-badge-red', violet: 'plate-badge-violet',
}

// Maps a column's `format` (an Ingredient's own name) to its display decimal places, matching the
// fixed catalog every business gets (see brainer's default-ingredients.ts) — "Decimal, 2 points"
// -> 2, "Integer" -> 0. Anything else (Text, Date, _document, ...) isn't a number column.
// Exported so BrainerPlateDetail's ViewWizard can reuse the exact same mapping at discovery time.
export function ingredientNameToDecimals(format?: string): number | undefined {
  if (!format) return undefined
  if (format === 'Integer') return 0
  const m = format.match(/^Decimal, (\d) points?$/)
  return m ? Number(m[1]) : undefined
}

// '_document' (Funnel uploads the source value directly) and '_documentPath' (Funnel fetches a
// remote path over SFTP first) are two different ingestion mechanisms, but both end with the
// same thing stored in the bean: a storage-service document id — see default-ingredients.ts's
// own description of each. Render both the same way.
function isDocumentFormat(format?: string): boolean {
  return format === '_document' || format === '_documentPath'
}

// Fixed decimal places + space-grouped thousands (1103.52 -> "1 103.52"), matching the trait's
// own ingredient precision rather than any locale.
export function formatDecimal(raw: unknown, decimals: number): string {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw))
  if (isNaN(n)) return String(raw)
  const [intPart, fracPart] = n.toFixed(decimals).split('.')
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return fracPart ? `${grouped}.${fracPart}` : grouped
}

function renderCellValue(def: BPlateViewColumnDef | undefined, raw: unknown, formatted: string) {
  const color = def?.badgeMap?.[String(raw)]
  if (color && BADGE_CLASS[color]) {
    return <span className={`inline-block border rounded-full px-2 py-0.5 text-theme-xs font-medium ${BADGE_CLASS[color]}`}>{formatted}</span>
  }
  return formatted
}

// View / Download for one document in a table cell. Both used to be fire-and-forget, so any
// failure (network, auth, a blocked popup) was invisible — now each shows a busy state and, on
// failure, the reason inline.
function CompactDocumentActions({ documentId, title }: { documentId: string; title?: string }) {
  const [busy, setBusy] = useState<'view' | 'download' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = (kind: 'view' | 'download', action: () => Promise<void>) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setError(null)
    setBusy(kind)
    action()
      .catch((err: unknown) => {
        const res = (err as { response?: { status?: number; data?: { error?: string } } })?.response
        const reason = res?.data instanceof Blob ? `HTTP ${res.status}` : res?.data?.error ?? (res?.status ? `HTTP ${res.status}` : (err as Error)?.message)
        console.error('Document action failed', { documentId, kind, err })
        setError(`Couldn't ${kind === 'view' ? 'open' : 'download'} document${reason ? ` — ${reason}` : ''}`)
      })
      .finally(() => setBusy(null))
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        <button
          onClick={run('view', () => viewDocumentInTab(documentId))}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 text-theme-sm font-medium text-brand-500 hover:text-brand-600 disabled:opacity-60"
          title={title ?? 'View document'}
        >
          {busy === 'view' ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />} View
        </button>
        <button
          onClick={run('download', () => downloadDocument(documentId, title ?? 'document'))}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 text-theme-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-60"
          title="Download"
        >
          {busy === 'download' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download
        </button>
      </div>
      {error && <span className="text-theme-xs text-error-500 whitespace-normal">{error}</span>}
    </div>
  )
}

// A "_document" column's value is a storage-service document id (see storage.service.ts's
// uploadDocument) — render the document itself instead of the raw id string. `compact` is used in
// TableView, where one row per document rules out an inline preview: just a "View" action there.
// Elsewhere (Card/List — a single-document field, e.g. "Purchase Order Form") the actual document
// renders inline: a PDF gets an <iframe> preview, anything else a small file card + Download.
function DocumentCell({ documentId, compact }: { documentId: string; compact?: boolean }) {
  const { data: doc } = useQuery({
    queryKey: ['storage-document', documentId],
    queryFn: () => getDocument(documentId).then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState(false)
  const isPdf = doc?.mimeType === 'application/pdf'

  useEffect(() => {
    if (compact || !isPdf) return
    let objectUrl: string | null = null
    let cancelled = false
    getDocumentBlobUrl(documentId).then(url => {
      if (cancelled) { URL.revokeObjectURL(url); return }
      objectUrl = url
      setPreviewUrl(url)
    }).catch(() => setPreviewError(true))
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [documentId, compact, isPdf])

  if (compact) {
    return <CompactDocumentActions documentId={documentId} title={doc?.title} />
  }

  if (isPdf) {
    if (previewError) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-red-500">
          <AlertCircleIcon size={12} /> Failed to load document
        </div>
      )
    }
    if (!previewUrl) {
      return (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={18} className="animate-spin text-ink-dim" />
        </div>
      )
    }
    return (
      <div className="space-y-1.5">
        <iframe src={previewUrl} title={doc?.title ?? 'Document'} className="w-full h-[600px] rounded-lg border border-paper-border" />
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" icon={Download} onClick={() => downloadDocument(documentId, doc?.title ?? 'document')}>
            Download
          </Button>
        </div>
      </div>
    )
  }

  // Non-PDF document (e.g. a legacy funnel-imported file) — no generic inline preview, just a
  // file card with the metadata already fetched and a Download action.
  return (
    <div className="flex items-center justify-between gap-2 border border-paper-border rounded-lg px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <FileText size={16} className="text-ink-dim shrink-0" />
        <span className="text-sm text-ink truncate">{doc?.title ?? documentId}</span>
      </div>
      <Button variant="secondary" size="sm" icon={Download} onClick={() => downloadDocument(documentId, doc?.title ?? 'document')}>
        Download
      </Button>
    </div>
  )
}

// Icon names a card view's columnConfig can opt into via `icon` (nav-tile mode). Deliberately a
// broader palette than any single Plate needs today, same spirit as BrainerPlateDetail's own
// BUTTON_ICONS registry, so future nav-hub Plates can pick a fitting icon without another
// code change here.
const NAV_TILE_ICONS: Record<string, LucideIcon> = {
  FileText, Wrench, ArrowLeftRight, Bell, Package, BarChart3, Users, MapPin, Truck,
  Receipt, ClipboardList, Calendar, Building2, Tag, Settings, ShoppingCart, Mail, Phone,
}

// Shared by PlateViewRenderer and PlateLayout's section-header buttons — both need to know
// whether they're inside OpenPlace or a dedicated per-module workspace so card/row-action/button
// forwarding stays inside the same shell instead of dropping into the Brainer console.
// Any dedicated per-module workspace (Garage, and future ones) mounts its own clean plate viewer
// at "<workspace>/plates/:id" (see PlateViewerPage.tsx) — matching that exact shape here (anchored
// at the end, so it does NOT match Brainer console's own "/brainer/plates/:id/run", which has a
// trailing segment) keeps card/row-action/forwarding navigation inside whatever workspace shell
// the viewer is currently under, with no per-module wiring needed in this shared component: the
// module just needs to mount PlateViewerPage at that URL shape.
export function buildPlateUrl(pathname: string, isOpenPlace: boolean, plateId: string, qs: string): string {
  const workspaceMatch = !isOpenPlace ? pathname.match(/^(\/[^/]+)\/plates\/[^/]+$/) : null
  return isOpenPlace
    ? `/open/${plateId}${qs}`
    : workspaceMatch
      ? `${workspaceMatch[1]}/plates/${plateId}${qs}`
      : `/brainer/plates/${plateId}/run${qs}`
}

export function usePlateNavigateTo() {
  const navigate = useNavigate()
  const location = useLocation()
  const isOpenPlace = location.pathname.startsWith('/open/')
  // paramName may be empty: a link to a page that takes no input (e.g. a Home tile).
  const navigateTo = (plateId: string, paramName: string, value: string) => {
    const qs = paramName ? `?${paramName}=${encodeURIComponent(value)}` : ''
    navigate(buildPlateUrl(location.pathname, isOpenPlace, plateId, qs))
  }
  return { navigateTo, isOpenPlace }
}

export function PlateViewRenderer({ result, runInputs, journeyApi = DEFAULT_JOURNEY_API }: Props) {
  const { view, data, error } = result
  const [chartType, setChartType] = useState<BPlateChartType>(view.chartType)
  const [selectedRow, setSelectedRow] = useState<Record<string, unknown> | null>(null)

  // Apply dataPath extraction (supports dot-notation, e.g. "data" or "result.items")
  const resolvedData = (() => {
    if (!data || typeof data !== 'object') return data
    if (!view.dataPath) return data
    const result = getByDotPath(data as Record<string, unknown>, view.dataPath)
    return result !== undefined ? result : data
  })()

  const containerStyle: React.CSSProperties = {
    maxHeight: view.maxHeight > 0 ? `${view.maxHeight}px` : undefined,
    overflowY: 'auto',
  }

  const { navigateTo, isOpenPlace } = usePlateNavigateTo()

  if (error) {
    return (
      <div className="rounded border border-red-200 bg-red-50 p-3 space-y-1.5">
        <p className="text-xs font-semibold text-red-600">Script execution error</p>
        <p className="text-xs text-red-500 font-mono break-all">{error}</p>
        <p className="text-theme-xs text-red-400">
          Drinker scripts only have access to the plate's input parameters as variables.
          Built-in globals (fetch, require, process, …) and any external names are not available.
        </p>
      </div>
    )
  }

  if (view.category === 'preparation') {
    return (
      <PreparationTriggerView
        title={view.title}
        preparationId={view.preparationId ?? ''}
        runInputs={runInputs}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        journeyApi={journeyApi}
        inline={view.inline}
      />
    )
  }

  // A button/form view has no drinkerId/inputParamId — runView() always returns {data: null}
  // for them (see plate.use-cases.ts), so both must be special-cased here too, before the
  // "no data" check below would otherwise swallow them.
  if (view.category === 'button') {
    return (
      <ButtonView
        title={view.title}
        preparationId={view.preparationId}
        runInputs={runInputs}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        journeyApi={journeyApi}
      />
    )
  }

  if (view.category === 'form') {
    return (
      <FormView
        fields={view.formFields ?? []}
        preparationId={view.preparationId}
        runInputs={runInputs}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        journeyApi={journeyApi}
      />
    )
  }

  if (resolvedData === null || resolvedData === undefined) {
    return <EmptyState message="No data yet" />
  }

  const hasRowActions = view.category === 'table' && (view.rowActions?.length ?? 0) > 0

  return (
    <div>
      {view.category === 'list'   && <ListView   data={resolvedData} style={containerStyle} columnConfig={view.columnConfig} navigateTo={navigateTo} />}
      {view.category === 'table'  && (
        <TableView
          data={resolvedData}
          style={containerStyle}
          columnConfig={view.columnConfig}
          navigateTo={navigateTo}
          onRowClick={hasRowActions ? (row) => setSelectedRow(row) : undefined}
          summaryRows={view.summaryRows}
        />
      )}
      {view.category === 'chart'  && (
        <ChartView data={resolvedData} chartType={chartType} onChartTypeChange={setChartType} />
      )}
      {view.category === 'card'   && <CardView  data={resolvedData} style={containerStyle} columnConfig={view.columnConfig} navigateTo={navigateTo} />}
      {view.category === 'text'   && <TextView  value={resolvedData} />}
      {view.category === 'title'  && <TitleView value={resolvedData} />}

      {selectedRow && view.rowActions && (
        <RowActionModal
          row={selectedRow}
          actions={view.rowActions}
          columnConfig={view.columnConfig}
          onClose={() => setSelectedRow(null)}
          navigateTo={navigateTo}
          isOpenPlace={isOpenPlace}
          runInputs={runInputs}
          journeyApi={journeyApi}
        />
      )}
    </div>
  )
}

// ─── PreparationTriggerView ────────────────────────────────────────────────────
// A 'preparation' category view used to auto-trigger its Journey inline the moment the Plate
// loaded — meaning e.g. "Create Preorder" permanently occupied a whole section with a live form,
// instead of behaving like the "+ Nouveau" button a user actually expects (confirmed against the
// legacy Passpartout UI this module replaces). Now it's just a button; clicking it opens a modal
// that mounts RowActionJourney fresh each time (same trigger-immediately-on-mount behavior, just
// deferred until the user asks for it) with an empty initialPayload — reusing the exact
// trigger/poll/interviewer/done flow row actions already use, rather than a second copy of it.

// Shared with PlateLayout.tsx's SectionButton for compact/global-button text coloring — a
// discrete navbar button still needs to read as "danger"/"warning" etc. (e.g. "Supprimer" red,
// "Email" amber, matching the legacy Passpartout toolbar this replaces).
export const COMPACT_VARIANT_TEXT: Record<string, string> = {
  danger: 'text-red-500 hover:text-red-600',
  warning: 'text-amber-500 hover:text-amber-600',
  success: 'text-emerald-600 hover:text-emerald-700',
  primary: 'text-accent hover:text-accent-hover',
}

export function PreparationTriggerView({ title, preparationId, runInputs, navigateTo, isOpenPlace, journeyApi, compact, icon: Icon, variant, inline }: {
  title: string
  preparationId: string
  runInputs?: Record<string, string>
  navigateTo: (plateId: string, paramName: string, value: string) => void
  isOpenPlace: boolean
  journeyApi: JourneyApi
  // compact: discrete icon-over-label rendering for a Plate's global buttons (a workspace
  // navbar), instead of the bold btn-primary used inline within a section.
  compact?: boolean
  icon?: LucideIcon
  variant?: string
  // Render the form right on the page, started with the Plate's inputs (e.g. an Edit Article page
  // opened with ?articleId=…, prefilled) instead of a button that opens it in a modal. Cancel —
  // or finishing without a forwarding Plate — goes back to wherever the user came from.
  inline?: boolean
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  if (!preparationId) {
    return <div className="p-3 text-xs text-ink-dim">No preparation configured</div>
  }

  if (inline) {
    return (
      <RowActionJourney
        preparationId={preparationId}
        initialPayload={{}}
        onDone={() => { qc.invalidateQueries({ queryKey: ['plate-view-run'] }); window.history.back() }}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        runInputs={runInputs}
        journeyApi={journeyApi}
      />
    )
  }

  const handleDone = () => {
    setOpen(false)
    // A completed Preparation (e.g. a new Preorder) should show up in the dashboard's table
    // without a full page reload. Completions are rare, user-driven events, so invalidating every
    // currently-mounted plate view is cheap and simpler than threading a narrower key through.
    qc.invalidateQueries({ queryKey: ['plate-view-run'] })
  }

  return (
    <>
      {compact ? (
        <button
          onClick={() => setOpen(true)}
          className={`flex flex-col items-center gap-1 rounded-lg px-2.5 py-1.5 hover:bg-gray-100 transition-colors ${COMPACT_VARIANT_TEXT[variant ?? ''] ?? 'text-ink-dim'}`}
          title={title}
        >
          {Icon && <Icon size={15} />}
          <span className="text-theme-xs leading-none font-medium whitespace-nowrap">{title}</span>
        </button>
      ) : (
        <button onClick={() => setOpen(true)} className={`${variant ? `btn-${variant}` : 'btn-primary'} flex items-center gap-1.5`}>
          {Icon && <Icon size={13} />}
          {title}
        </button>
      )}
      {open && (
        <Modal title={title} onClose={() => setOpen(false)} size="md">
          <RowActionJourney
            preparationId={preparationId}
            initialPayload={{}}
            onDone={handleDone}
            navigateTo={navigateTo}
            isOpenPlace={isOpenPlace}
            runInputs={runInputs}
            journeyApi={journeyApi}
          />
        </Modal>
      )}
    </>
  )
}

// ─── List ─────────────────────────────────────────────────────────────────────

function ListView({ data, style, columnConfig, navigateTo }: {
  data: unknown
  style: React.CSSProperties
  columnConfig?: BPlateViewColumnDef[]
  navigateTo: (plateId: string, paramName: string, value: string) => void
}) {
  const fmtDate = useDateFmt()
  if (Array.isArray(data)) {
    if (data.length === 0) return <EmptyState message="No items" />

    const firstRow = data[0]
    if (typeof firstRow !== 'object' || firstRow === null) {
      return (
        <div style={style} className="divide-y divide-paper-border">
          {data.map((item, i) => (
            <div key={i} className="py-1.5 px-2 text-sm text-ink">{String(item)}</div>
          ))}
        </div>
      )
    }

    const firstRowObj = firstRow as Record<string, unknown>
    const configMap  = new Map(columnConfig?.map(c => [c.key, c]) ?? [])
    const displayCols = columnConfig?.length
      ? columnConfig.map(c => c.key).filter(k => getByDotPath(firstRowObj, k) !== undefined)
      : Object.keys(firstRowObj)

    return (
      <div style={style} className="divide-y divide-paper-border">
        {(data as Record<string, unknown>[]).map((row, i) => (
          <div key={i} className="py-1.5 px-2 text-sm text-ink flex flex-wrap gap-x-4 gap-y-0.5">
            {displayCols.map(col => {
              const def   = configMap.get(col)
              const label = def?.label ?? col
              let raw     = getByDotPath(row, col)
              if ((raw === null || raw === undefined) && def?.fallbackKey) raw = getByDotPath(row, def.fallbackKey)
              if (isDocumentFormat(def?.format) && raw) {
                return (
                  <span key={col} className="w-full">
                    <span className="text-ink-dim text-xs">{label}: </span>
                    <DocumentCell documentId={String(raw)} compact />
                  </span>
                )
              }
              const decimals = ingredientNameToDecimals(def?.format)
              const value = def?.isDate ? fmtDate(raw as string | null) : (raw === null || raw === undefined ? (def?.hideWhenEmpty ? '' : '—') : decimals !== undefined ? formatDecimal(raw, decimals) : String(raw))
              if (def?.isLink && def.linkPlateId) {
                const resolved = resolveTemplate(def.linkValueTemplate ?? '', row)
                return (
                  <span key={col}>
                    <span className="text-ink-dim text-xs">{label}: </span>
                    <button onClick={() => navigateTo(def.linkPlateId!, def.linkInputParamName ?? '', resolved)} className="text-accent hover:underline">{value}</button>
                  </span>
                )
              }
              return (
                <span key={col}>
                  <span className="text-ink-dim text-xs">{label}: </span>{renderCellValue(def, raw, value)}
                </span>
              )
            })}
          </div>
        ))}
      </div>
    )
  }

  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>
    const configMap  = new Map(columnConfig?.map(c => [c.key, c]) ?? [])
    const displayKeys = columnConfig?.length
      ? columnConfig.map(c => c.key).filter(k => getByDotPath(obj, k) !== undefined)
      : Object.keys(obj)

    return (
      <div style={style} className="divide-y divide-paper-border">
        {displayKeys.map(key => {
          const def   = configMap.get(key)
          const label = def?.label ?? key
          let raw     = getByDotPath(obj, key)
          if ((raw === null || raw === undefined) && def?.fallbackKey) raw = getByDotPath(obj, def.fallbackKey)
          if (isDocumentFormat(def?.format) && raw) {
            return (
              <div key={key} className="py-1.5 px-2 text-sm space-y-1">
                <span className="text-ink-dim">{label}</span>
                <DocumentCell documentId={String(raw)} />
              </div>
            )
          }
          const decimals = ingredientNameToDecimals(def?.format)
          const value = def?.isDate ? fmtDate(raw as string | null) : (raw === null || raw === undefined ? (def?.hideWhenEmpty ? '' : '—') : decimals !== undefined ? formatDecimal(raw, decimals) : String(raw))
          if (def?.isLink && def.linkPlateId) {
            const resolved = resolveTemplate(def.linkValueTemplate ?? '', obj)
            return (
              <div key={key} className="flex items-center justify-between py-1.5 px-2 text-sm">
                <span className="text-ink-dim">{label}</span>
                <button onClick={() => navigateTo(def.linkPlateId!, def.linkInputParamName ?? '', resolved)} className="text-accent hover:underline font-medium">{value}</button>
              </div>
            )
          }
          return (
            <div key={key} className="flex items-center justify-between py-1.5 px-2 text-sm">
              <span className="text-ink-dim">{label}</span>
              <span className="font-medium text-ink tabular-nums">{renderCellValue(def, raw, value)}</span>
            </div>
          )
        })}
      </div>
    )
  }

  return <div className="p-2 text-sm text-ink">{String(data)}</div>
}

// ─── Table ────────────────────────────────────────────────────────────────────

// Invoice-style totals under a table: label/value pairs right-aligned under the last column, the
// emphasized (grand total) line bold with a rule above. Aggregates over the rows the table shows.
function aggregate(rows: Record<string, unknown>[], def: BPlateViewSummaryRow): number {
  if (def.agg === 'count') return rows.length
  const nums = rows.map(r => parseFloat(String(getByDotPath(r, def.key) ?? ''))).filter(n => !isNaN(n))
  if (def.agg === 'min') return nums.length ? Math.min(...nums) : 0
  if (def.agg === 'max') return nums.length ? Math.max(...nums) : 0
  const sum = nums.reduce((a, b) => a + b, 0)
  return def.agg === 'avg' ? (nums.length ? sum / nums.length : 0) : sum
}

function SummaryBlock({ rows, summaryRows }: { rows: Record<string, unknown>[]; summaryRows: BPlateViewSummaryRow[] }) {
  return (
    <div className="flex justify-end border-t border-gray-100 px-5 py-4 sm:px-6">
      <dl className="w-full max-w-xs space-y-2 text-sm tabular-nums">
        {summaryRows.map((def, i) => {
          const value = aggregate(rows, def)
          const decimals = ingredientNameToDecimals(def.format) ?? (def.agg === 'count' ? 0 : 2)
          return (
            <div
              key={i}
              className={`flex items-baseline justify-between gap-6 ${def.emphasis ? 'border-t border-gray-200 pt-2 text-base font-semibold text-gray-800' : 'text-gray-500'}`}
            >
              <dt>{def.label}</dt>
              <dd className={def.emphasis ? '' : 'font-medium text-gray-700'}>{formatDecimal(value, decimals)}</dd>
            </div>
          )
        })}
      </dl>
    </div>
  )
}

function TableView({ data, style, columnConfig, navigateTo, onRowClick, summaryRows }: {
  data: unknown
  style: React.CSSProperties
  columnConfig?: BPlateViewColumnDef[]
  navigateTo: (plateId: string, paramName: string, value: string) => void
  onRowClick?: (row: Record<string, unknown>) => void
  summaryRows?: BPlateViewSummaryRow[]
}) {
  const fmtDate = useDateFmt()
  if (!Array.isArray(data) || data.length === 0) {
    return <EmptyState message="Empty table" />
  }

  const firstRow = data[0]
  if (typeof firstRow !== 'object' || firstRow === null) {
    return <ListView data={data} style={style} columnConfig={columnConfig} navigateTo={navigateTo} />
  }

  const firstRowObj = firstRow as Record<string, unknown>
  const configMap  = new Map(columnConfig?.map(c => [c.key, c]) ?? [])
  const displayCols = columnConfig?.length
    ? columnConfig.map(c => c.key).filter(k => getByDotPath(firstRowObj, k) !== undefined)
    : Object.keys(firstRowObj)

  return (
    <div style={style} className="table-wrap">
      <table className="plate-table">
        <thead>
          <tr>
            {displayCols.map(col => (
              <th key={col} className={ingredientNameToDecimals(configMap.get(col)?.format) !== undefined ? 'text-right' : undefined}>{configMap.get(col)?.label ?? col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data as Record<string, unknown>[]).map((row, i) => (
            <tr
              key={i}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'cursor-pointer' : ''}
            >
              {displayCols.map(col => {
                const def   = configMap.get(col)
                let raw     = getByDotPath(row, col)
                if ((raw === null || raw === undefined) && def?.fallbackKey) raw = getByDotPath(row, def.fallbackKey)
                if (isDocumentFormat(def?.format) && raw) {
                  return <td key={col}><DocumentCell documentId={String(raw)} compact /></td>
                }
                const decimals = ingredientNameToDecimals(def?.format)
                const value = def?.isDate ? fmtDate(raw as string | null) : (raw === null || raw === undefined ? (def?.hideWhenEmpty ? '' : '—') : decimals !== undefined ? formatDecimal(raw, decimals) : String(raw))
                const alignRight = decimals !== undefined
                if (def?.isLink && def.linkPlateId) {
                  const resolved = resolveTemplate(def.linkValueTemplate ?? '', row)
                  return (
                    <td key={col} className={alignRight ? 'text-right' : undefined}>
                      <button
                        onClick={(e) => { e.stopPropagation(); navigateTo(def.linkPlateId!, def.linkInputParamName ?? '', resolved) }}
                        className="text-accent hover:underline"
                      >
                        {value}
                      </button>
                    </td>
                  )
                }
                return (
                  <td key={col} className={alignRight ? 'text-right' : undefined}>{renderCellValue(def, raw, value)}</td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {summaryRows && summaryRows.length > 0 && (
        <SummaryBlock rows={data as Record<string, unknown>[]} summaryRows={summaryRows.filter(r => r.key)} />
      )}
    </div>
  )
}

// ─── Chart ────────────────────────────────────────────────────────────────────

function normalizeChartData(data: unknown): { name: string; value: number }[] {
  if (Array.isArray(data)) {
    return data.map((item, i) => {
      if (typeof item === 'object' && item !== null) {
        const entries = Object.entries(item as Record<string, unknown>)
        const nameEntry = entries.find(([, v]) => typeof v === 'string')
        const valEntry  = entries.find(([, v]) => typeof v === 'number')
        return {
          name:  nameEntry ? String(nameEntry[1]) : `Item ${i + 1}`,
          value: valEntry  ? Number(valEntry[1])  : 0,
          ...(item as Record<string, unknown>),
        }
      }
      return { name: `Item ${i + 1}`, value: Number(item) || 0 }
    })
  }
  if (typeof data === 'object' && data !== null) {
    return Object.entries(data as Record<string, unknown>).map(([k, v]) => ({
      name: k,
      value: Number(v) || 0,
    }))
  }
  return []
}

export function ChartView({ data, chartType, onChartTypeChange }: {
  data: unknown
  chartType: BPlateChartType
  onChartTypeChange: (t: BPlateChartType) => void
}) {
  const chartData = normalizeChartData(data)
  const keys = chartData.length > 0 ? Object.keys(chartData[0]).filter(k => k !== 'name') : ['value']

  const TYPES: BPlateChartType[] = ['bar', 'line', 'pie', 'area']

  return (
    <div>
      <div className="flex gap-1 mb-2">
        {TYPES.map((t) => (
          <button
            key={t}
            onClick={() => onChartTypeChange(t)}
            className={`px-2 py-0.5 text-theme-xs rounded border transition-colors ${
              chartType === t
                ? 'bg-accent text-white border-accent'
                : 'bg-white text-ink-dim border-paper-border hover:border-paper-border'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={260}>
        {chartType === 'bar' ? (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
            <Tooltip />
            {keys.map((k, i) => <Bar key={k} dataKey={k} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[2,2,0,0]} />)}
          </BarChart>
        ) : chartType === 'line' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
            <Tooltip />
            {keys.map((k, i) => <Line key={k} dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} dot={{ r: 3 }} strokeWidth={2} />)}
          </LineChart>
        ) : chartType === 'area' ? (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
            <Tooltip />
            {keys.map((k, i) => (
              <Area key={k} dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]}
                fill={CHART_COLORS[i % CHART_COLORS.length] + '30'} strokeWidth={2} />
            ))}
          </AreaChart>
        ) : (
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function CardView({ data, style, columnConfig, navigateTo }: {
  data: unknown
  style: React.CSSProperties
  columnConfig?: BPlateViewColumnDef[]
  navigateTo?: (plateId: string, paramName: string, value: string) => void
}) {
  const fmtDate = useDateFmt()
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const entries = Object.entries(data as Record<string, unknown>)
    const configMap = new Map(columnConfig?.map(c => [c.key, c]) ?? [])
    const hasLinks = entries.some(([key]) => configMap.get(key)?.isLink)
    const hasGroups = !hasLinks && entries.some(([key]) => configMap.get(key)?.group)

    // Nav-tile mode: a card view whose columns are destinations, not metrics (e.g. a Home hub) —
    // each entry is its own bordered tile with an optional icon badge and a visible description,
    // instead of the plain de-boxed KPI-tile style below.
    if (hasLinks) {
      return (
        <div style={style} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4">
          {entries.map(([key, value]) => {
            const def = configMap.get(key)
            const Icon = def?.icon ? NAV_TILE_ICONS[def.icon] : undefined
            // Prefer an explicit description; fall back to the entry's own value, which for a
            // drinker-fed nav hub (e.g. Home) is already a short descriptive caption, not a raw
            // number — showing it directly avoids maintaining the same text in two places.
            const caption = def?.description ?? (value !== null && value !== undefined ? String(value) : undefined)
            const tileBody = (
              <>
                {Icon && (
                  <div className="w-10 h-10 rounded-full bg-accent-soft flex items-center justify-center mb-3">
                    <Icon size={18} className="text-accent" />
                  </div>
                )}
                <p className="text-base font-semibold text-gray-800">{def?.label ?? key}</p>
                {caption && <p className="text-ink-dim text-xs mt-1">{caption}</p>}
              </>
            )
            if (def?.isLink && def.linkPlateId && navigateTo) {
              const resolved = resolveTemplate(def.linkValueTemplate ?? '', data as Record<string, unknown>)
              return (
                <button
                  key={key}
                  onClick={() => navigateTo(def.linkPlateId!, def.linkInputParamName ?? '', resolved)}
                  className="plate-card text-left p-5 hover:shadow-lg transition-shadow"
                >
                  {tileBody}
                </button>
              )
            }
            return (
              <div key={key} className="plate-card p-5">
                {tileBody}
              </div>
            )
          })}
        </div>
      )
    }

    // Grouped document mode: a card view whose columns describe a record (e.g. a quote header) —
    // reads like an actual document instead of a dense data table. Any ungrouped field is
    // secondary metadata shown as a plain label/value pair above the groups. Each named group
    // (e.g. "Client") becomes its own untitled block — no heading text, just its fields stacked
    // as discrete, minimal label/value pairs — aligned side by side with a thin rule between
    // them, the way a real quote/invoice separates "from / to / terms" without boxing each one.
    // Order within each block follows the Plate author's columnConfig, not incidental data-key
    // order; any field the drinker returns with no matching columnConfig entry still shows,
    // appended after the configured ones, so nothing silently disappears.
    if (hasGroups) {
      const dataRecord = data as Record<string, unknown>
      const cfg = columnConfig ?? []
      const configuredKeys = new Set(cfg.map((c) => c.key))
      const extra = entries.filter(([key]) => !configuredKeys.has(key))
      const ungrouped: [string, unknown][] = [...cfg.filter((c) => !c.group).map((c) => [c.key, dataRecord[c.key]] as [string, unknown]), ...extra]
      const groupedCfg = cfg.filter((c) => c.group)
      const groupOrder: string[] = []
      for (const c of groupedCfg) if (!groupOrder.includes(c.group!)) groupOrder.push(c.group!)
      const fmt = (def: BPlateViewColumnDef | undefined, value: unknown) => {
        const decimals = ingredientNameToDecimals(def?.format)
        return value === null || value === undefined ? (def?.hideWhenEmpty ? '' : '—')
          : def?.isDate ? fmtDate(value as string | null)
          : decimals !== undefined ? formatDecimal(value, decimals)
          : String(value)
      }

      const field = (key: string, value: unknown) => {
        const def = configMap.get(key)
        if ((value === null || value === undefined) && def?.fallbackKey) value = getByDotPath(dataRecord, def.fallbackKey)
        if (isDocumentFormat(def?.format) && value) {
          return (
            <div key={key}>
              {def?.showLabel !== false && <p className="text-[10px] text-ink-dim uppercase tracking-wide mb-1">{def?.label ?? key}</p>}
              <DocumentCell documentId={String(value)} />
            </div>
          )
        }
        const rendered = renderCellValue(def, value, fmt(def, value))
        if (def?.showLabel === false) {
          return <p key={key} className="text-sm text-ink">{rendered}</p>
        }
        if (def?.labelInline) {
          return (
            <p key={key} className="text-sm text-ink">
              <span className="text-ink-dim">{def?.label ?? key}: </span>{rendered}
            </p>
          )
        }
        return (
          <div key={key}>
            <p className="text-[10px] text-ink-dim uppercase tracking-wide">{def?.label ?? key}</p>
            <p className="text-sm text-ink">{rendered}</p>
          </div>
        )
      }

      return (
        <div style={style}>
          {ungrouped.length > 0 && (
            <div className="flex flex-wrap gap-x-6 gap-y-3 pb-4 mb-4 border-b border-paper-border">
              {ungrouped.map(([key, value]) => field(key, value))}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 divide-y sm:divide-y-0 sm:divide-x divide-paper-border">
            {groupOrder.map((g) => (
              <div key={g} className="space-y-2.5 pt-4 first:pt-0 sm:pt-0 sm:first:pl-0 sm:pl-6">
                {groupedCfg.filter((c) => c.group === g).map((c) => field(c.key, dataRecord[c.key]))}
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div style={style} className="grid grid-cols-2 gap-x-8 gap-y-6">
        {entries.map(([key, value]) => {
          const def = configMap.get(key)
          if (isDocumentFormat(def?.format) && value) {
            return (
              <div key={key} className="col-span-2">
                <p className="text-[10px] font-semibold text-ink-dim uppercase tracking-widest mb-1">{def?.label ?? key}</p>
                <DocumentCell documentId={String(value)} />
              </div>
            )
          }
          const decimals = ingredientNameToDecimals(def?.format)
          const displayValue = def?.isDate ? fmtDate(value as string | null)
            : decimals !== undefined && value !== null && value !== undefined ? formatDecimal(value, decimals)
            : String(value)
          const body = (
            <>
              <p className="text-[10px] font-semibold text-ink-dim uppercase tracking-widest mb-1">{def?.label ?? key}</p>
              <p className={`text-2xl font-bold text-ink tabular-nums truncate ${decimals !== undefined ? 'text-right' : ''}`}>{renderCellValue(def, value, displayValue)}</p>
            </>
          )
          if (def?.isLink && def.linkPlateId && navigateTo) {
            const resolved = resolveTemplate(def.linkValueTemplate ?? '', data as Record<string, unknown>)
            return (
              <button
                key={key}
                onClick={() => navigateTo(def.linkPlateId!, def.linkInputParamName ?? '', resolved)}
                title={def?.description}
                className="text-left -m-2 p-2 rounded-lg hover:bg-paper-sunken transition-colors"
              >
                {body}
              </button>
            )
          }
          return (
            <div key={key} title={def?.description}>
              {body}
            </div>
          )
        })}
      </div>
    )
  }
  if (Array.isArray(data)) {
    return (
      <div style={style} className="grid grid-cols-2 gap-x-8 gap-y-6">
        {(data as unknown[]).map((item, i) => (
          <div key={i} className="text-sm text-ink">
            {String(item)}
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="text-2xl font-bold text-ink text-center">
      {String(data)}
    </div>
  )
}

// ─── TextView / TitleView ─────────────────────────────────────────────────────
// Static content blocks driven by a drinker result + dataPath, same mechanism 'card'/'table'
// already use (PlateViewRenderer's own dataPath extraction above) — 'text' renders a <p>, 'title'
// an <h5>. A non-string result (e.g. dataPath pointing at an object) falls back to String(value).

function displayText(value: unknown): string {
  if (value === null || value === undefined) return ''
  return typeof value === 'string' ? value : String(value)
}

function TextView({ value }: { value: unknown }) {
  return <p className="text-sm text-ink">{displayText(value)}</p>
}

function TitleView({ value }: { value: unknown }) {
  return <h5 className="font-serif font-semibold text-ink">{displayText(value)}</h5>
}

// ─── RowActionModal ───────────────────────────────────────────────────────────

function RowActionModal({ row, actions, columnConfig, onClose, navigateTo, isOpenPlace, runInputs, journeyApi }: {
  row: Record<string, unknown>
  actions: BPlateViewRowAction[]
  columnConfig?: BPlateViewColumnDef[]
  onClose: () => void
  navigateTo: (plateId: string, paramName: string, value: string) => void
  isOpenPlace: boolean
  runInputs?: Record<string, string>
  journeyApi: JourneyApi
}) {
  const [activeAction, setActiveAction] = useState<BPlateViewRowAction | null>(null)
  const fmtDate = useDateFmt()

  const displayCols = columnConfig?.length
    ? columnConfig.map(c => ({ key: c.key, label: c.label, isDate: c.isDate, hideWhenEmpty: c.hideWhenEmpty, format: c.format }))
    : Object.keys(row).slice(0, 8).map(k => ({ key: k, label: k, isDate: false, hideWhenEmpty: false, format: undefined as string | undefined }))

  return (
    <Modal title="Row actions" onClose={onClose} size="md">
      {activeAction?.preparationId ? (
        <RowActionJourney
          preparationId={activeAction.preparationId}
          initialPayload={{ [activeAction.payloadKey]: String(getByDotPath(row, activeAction.idField) ?? '') }}
          onDone={onClose}
          navigateTo={navigateTo}
          isOpenPlace={isOpenPlace}
          runInputs={runInputs}
          journeyApi={journeyApi}
        />
      ) : (
        <div className="space-y-4">
          {/* Row summary */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 pb-3 border-b border-paper-border max-h-48 overflow-y-auto">
            {displayCols.map(({ key, label, isDate, hideWhenEmpty, format }) => {
              const raw = getByDotPath(row, key)
              if (isDocumentFormat(format) && raw) {
                return (
                  <div key={key} className="min-w-0">
                    <dt className="text-[10px] text-ink-dim truncate">{label}</dt>
                    <dd className="text-theme-xs"><DocumentCell documentId={String(raw)} compact /></dd>
                  </div>
                )
              }
              const decimals = ingredientNameToDecimals(format)
              const value = isDate ? fmtDate(raw as string | null)
                : (raw === null || raw === undefined) ? (hideWhenEmpty ? '' : '—')
                : decimals !== undefined ? formatDecimal(raw, decimals)
                : String(raw)
              return (
                <div key={key} className="min-w-0">
                  <dt className="text-[10px] text-ink-dim truncate">{label}</dt>
                  <dd className="text-theme-xs text-ink font-medium truncate">{value}</dd>
                </div>
              )
            })}
          </dl>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {actions
              .filter(action => !action.showIf || String(getByDotPath(row, action.showIf.field) ?? '') === action.showIf.equals)
              .map((action, i) => {
              const isDanger = action.variant === 'danger'
              if (action.destinationPlateId) {
                const idVal = String(getByDotPath(row, action.idField) ?? '')
                return (
                  <button
                    key={i}
                    onClick={() => navigateTo(action.destinationPlateId!, action.payloadKey, idVal)}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      isDanger
                        ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                        : 'bg-accent text-white hover:bg-accent-hover'
                    }`}
                  >
                    {action.label}
                  </button>
                )
              }
              return (
                <button
                  key={i}
                  onClick={() => { console.log('[RowAction] clicked', action); setActiveAction(action) }}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    isDanger
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      : 'bg-accent text-white hover:bg-accent-hover'
                  }`}
                >
                  {action.label}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </Modal>
  )
}

// ─── RowActionJourney ─────────────────────────────────────────────────────────

function RowActionJourney({ preparationId, initialPayload, onDone, navigateTo, isOpenPlace, runInputs, journeyApi }: {
  preparationId: string
  initialPayload: Record<string, string>
  onDone: () => void
  navigateTo: (plateId: string, paramName: string, value: string) => void
  isOpenPlace: boolean
  runInputs?: Record<string, string>
  journeyApi: JourneyApi
}) {
  const qc = useQueryClient()
  const location = useLocation()
  const [journeyId, setJourneyId] = useState<string | null>(null)
  const [journeyStatus, setJourneyStatus] = useState<string | null>(null)
  const started = useRef(false)

  const [triggerError, setTriggerError] = useState<string | null>(null)
  // A 422 on submit carries per-field errors the wizard shows on the fields themselves; any other
  // failure is shown above the form so the user isn't left with a silently dead Save button.
  const [serverErrors, setServerErrors] = useState<BCoffeeValidationError[] | undefined>()
  const [submitError, setSubmitError] = useState<string | null>(null)
  // A relation item's alternate action (e.g. "+ Add one-time article") — runs that Preparation in
  // a nested modal with this journey's inputs; when it finishes, it replaces this one.
  const [altAction, setAltAction] = useState<{ preparationId: string; label: string } | null>(null)

  const trigger = useMutation({
    mutationFn: () => journeyApi.trigger(preparationId, { ...runInputs, ...initialPayload }),
    onSuccess: (res) => { setJourneyId(res.data.id); setJourneyStatus(res.data.status) },
    onError: (err: any) => {
      const msg = err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? 'Failed to trigger preparation'
      setTriggerError(msg)
    },
  })

  useEffect(() => {
    if (!started.current) {
      started.current = true
      console.log('[RowActionJourney] triggering', preparationId, initialPayload)
      trigger.mutate()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: polledJourney } = useQuery({
    queryKey: ['row-action-journey-poll', journeyId],
    queryFn: () => journeyApi.get(journeyId!).then(r => r.data),
    enabled: !!journeyId && (journeyStatus === 'pending' || journeyStatus === 'running'),
    refetchInterval: 800,
    retry: false,
  })
  useEffect(() => {
    if (polledJourney && polledJourney.status !== 'pending') setJourneyStatus(polledJourney.status)
  }, [polledJourney])

  const { data: interviewerForm, isLoading: formLoading } = useQuery({
    queryKey: ['row-action-interviewer', journeyId],
    queryFn: () => journeyApi.getInterviewer(journeyId!).then(r => r.data),
    enabled: !!journeyId && journeyStatus === 'paused',
    retry: false,
  })

  const submitInterview = useMutation({
    mutationFn: (d: { traits: Record<string, string>; relations: Record<string, any>; chainedAnswers: Record<string, any>; globals: Record<string, any> }) =>
      journeyApi.submitInterviewer(journeyId!, d),
    onSuccess: (res) => {
      const { journey, output, forwardingPlateId } = res.data
      setJourneyStatus(journey.status)
      if (journey.status === 'completed' || journey.status === 'failed') {
        if (journey.status === 'completed' && forwardingPlateId) {
          const params = new URLSearchParams()
          for (const [k, v] of Object.entries(output ?? {})) {
            if (v !== null && v !== undefined) params.set(k, String(v))
          }
          const qs = params.toString()
          // Hard navigation (not react-router's navigate) — forwarding can land on a Plate outside
          // whatever's currently mounted, so a full reload is the safe way to pick up fresh data.
          // Still workspace-aware (buildPlateUrl), so completing e.g. "Create Client Company" from
          // inside Garage lands back in Garage's own workspace instead of the Brainer admin console.
          window.location.href = buildPlateUrl(location.pathname, isOpenPlace, forwardingPlateId, qs ? '?' + qs : '')
        } else {
          onDone()
        }
      } else if (journey.status === 'paused') {
        qc.invalidateQueries({ queryKey: ['row-action-interviewer', journeyId] })
      }
    },
    onMutate: () => { setSubmitError(null) },
    onError: (err: any) => {
      const data = err?.response?.data
      if (err?.response?.status === 422 && Array.isArray(data?.errors)) {
        setServerErrors([...data.errors])
      } else {
        setSubmitError(data?.error ?? err?.message ?? 'Saving failed')
      }
    },
  })

  const handleAltDone = () => {
    setAltAction(null)
    // The alternate Preparation did this form's job (e.g. created the preorder line itself), so
    // this journey is abandoned rather than left paused forever.
    if (journeyId) stopJourney(journeyId).catch(() => { /* already finished or gone */ })
    qc.invalidateQueries({ queryKey: ['plate-view-run'] })
    onDone()
  }

  if (triggerError) {
    return (
      <div className="py-6 text-center space-y-3">
        <p className="text-sm text-red-500">Could not trigger preparation</p>
        <p className="text-xs text-red-400 font-mono break-all">{triggerError}</p>
        <button onClick={onDone} className="text-xs text-ink-dim underline">Close</button>
      </div>
    )
  }

  if (!journeyId || journeyStatus === 'pending') {
    return <div className="flex items-center justify-center py-8"><Loader2 size={18} className="animate-spin text-ink-dim" /></div>
  }
  if (journeyStatus === 'running') {
    return <div className="flex items-center justify-center gap-2 py-6 text-sm text-accent"><Loader2 size={15} className="animate-spin" /> Running…</div>
  }
  if (journeyStatus === 'paused') {
    if (formLoading) return <div className="flex items-center justify-center gap-2 py-6 text-sm text-neutral-400"><Loader2 size={15} className="animate-spin" /> Loading form…</div>
    if (interviewerForm) {
      return (
        <>
          {interviewerForm.layout !== 'page' && (
            <p className="text-xs font-semibold text-neutral-600 mb-3">
              {interviewerForm.isUpdate ? 'Update' : 'New'} {interviewerForm.cupTitle}
            </p>
          )}
          {submitError && (
            <div className="mb-4 flex gap-3 rounded-xl border border-error-500 bg-error-50 p-4 text-sm text-gray-700">
              <AlertCircleIcon size={20} className="shrink-0 text-error-500" />
              <p><span className="font-semibold text-gray-800">Couldn't save — </span>{submitError}</p>
            </div>
          )}
          <InterviewerWizard
            form={interviewerForm}
            onSubmit={(traits, relations, chainedAnswers, globals) => submitInterview.mutate({ traits, relations, chainedAnswers, globals })}
            onCancel={onDone}
            isPending={submitInterview.isPending}
            navigateTo={navigateTo}
            serverErrors={serverErrors}
            onAltAction={(altPreparationId, label) => setAltAction({ preparationId: altPreparationId, label })}
          />
          {altAction && (
            <Modal title={altAction.label} onClose={() => setAltAction(null)} size="lg">
              <RowActionJourney
                preparationId={altAction.preparationId}
                initialPayload={initialPayload}
                onDone={handleAltDone}
                navigateTo={navigateTo}
                isOpenPlace={isOpenPlace}
                runInputs={runInputs}
                journeyApi={journeyApi}
              />
            </Modal>
          )}
        </>
      )
    }
  }
  if (journeyStatus === 'completed') {
    return (
      <div className="py-6 text-center space-y-3">
        <p className="text-sm text-green-600 font-medium">Done</p>
        <button onClick={onDone} className="text-xs text-ink-dim underline">Close</button>
      </div>
    )
  }
  if (journeyStatus === 'failed') {
    return (
      <div className="py-6 text-center space-y-3">
        <p className="text-sm text-red-500">Action failed</p>
        {polledJourney?.failureReason && <p className="text-sm text-gray-600">{polledJourney.failureReason}</p>}
        <button onClick={onDone} className="text-xs text-ink-dim underline">Close</button>
      </div>
    )
  }
  return null
}

// ─── Button ───────────────────────────────────────────────────────────────────
// Triggers view.preparationId on click (e.g. Phase 3b's Emergency Stop) — reuses
// RowActionJourney's own trigger+poll UI once clicked, same as a table row action does. A native
// confirm() is the whole safety net against accidental clicks; deliberately not a custom modal —
// this is a single irreversible-from-here action, not a form.

function ButtonView({ title, preparationId, runInputs, navigateTo, isOpenPlace, journeyApi }: {
  title: string
  preparationId?: string
  runInputs?: Record<string, string>
  navigateTo: (plateId: string, paramName: string, value: string) => void
  isOpenPlace: boolean
  journeyApi: JourneyApi
}) {
  const [triggered, setTriggered] = useState(false)

  if (!preparationId) {
    return <div className="p-3 text-xs text-ink-dim">No preparation configured</div>
  }

  if (triggered) {
    return (
      <RowActionJourney
        preparationId={preparationId}
        initialPayload={{}}
        onDone={() => setTriggered(false)}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        runInputs={runInputs}
        journeyApi={journeyApi}
      />
    )
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button onClick={() => { if (window.confirm(`${title}?`)) setTriggered(true) }}>
        {title}
      </Button>
    </div>
  )
}

// ─── Form ─────────────────────────────────────────────────────────────────────
// A small declarative text-field form that triggers view.preparationId on submit, the field
// values becoming the Journey's initialPayload — the generic multi-field counterpart to a
// button (single fixed action) or a row action (single id field). Reuses RowActionJourney's own
// trigger+poll UI once submitted, same as Button/RowAction do.

function FormView({ fields, preparationId, runInputs, navigateTo, isOpenPlace, journeyApi }: {
  fields: BPlateViewFormField[]
  preparationId?: string
  runInputs?: Record<string, string>
  navigateTo: (plateId: string, paramName: string, value: string) => void
  isOpenPlace: boolean
  journeyApi: JourneyApi
}) {
  const [values, setValues] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)

  if (!preparationId) {
    return <div className="p-3 text-xs text-ink-dim">No preparation configured</div>
  }

  if (submitted) {
    return (
      <RowActionJourney
        preparationId={preparationId}
        initialPayload={values}
        onDone={() => { setSubmitted(false); setValues({}) }}
        navigateTo={navigateTo}
        isOpenPlace={isOpenPlace}
        runInputs={runInputs}
        journeyApi={journeyApi}
      />
    )
  }

  return (
    <div className="p-3 space-y-3">
      {fields.map(field => (
        <div key={field.key}>
          <label className="block text-theme-xs font-medium text-ink-dim mb-1">{field.label}</label>
          <input
            type={field.inputType ?? 'text'}
            placeholder={field.placeholder}
            value={values[field.key] ?? ''}
            onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            className="w-full text-sm border border-paper-border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
      ))}
      <Button onClick={() => setSubmitted(true)}>Submit</Button>
    </div>
  )
}
